import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatCLP } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/contexts/ConfigContext";
import { BadgeCheck, Clock, MapPin, Plus, Star, Briefcase, Handshake, Crown, Sparkles, Zap, Shield, ArrowUpRight, HeartHandshake } from "lucide-react";
import PioneroChallengeCard from "@/components/PioneroChallengeCard";

export default function Dashboard() {
  const { user } = useAuth();
  const { config } = useConfig();
  const [open, setOpen] = useState([]);
  const [mine, setMine] = useState([]);          // delegations I published
  const [assignedToMe, setAssignedToMe] = useState([]);  // delegations assigned TO me (active work)
  const [activeMediations, setActiveMediations] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        if (user?.role === "lawyer") {
          // Active work assigned to me — the most important section
          const a = await api.get("/delegations", { params: { mine: "assigned" } });
          setAssignedToMe((a.data.items || []).filter(d => d.status !== "completed" && d.status !== "cancelled").slice(0, 8));
          // My published delegations
          const m = await api.get("/delegations", { params: { mine: "owned" } });
          setMine((m.data.items || []).slice(0, 5));
        }
        // Directorio: open delegations (solo para abogados/admin — módulo exclusivo)
        if (user?.role === "lawyer" || user?.role === "admin") {
          const o = await api.get("/delegations", { params: { status: "open" } });
          setOpen((o.data.items || []).slice(0, 6));
        }
        // My mediations (any role)
        if (user?.role === "lawyer" || user?.role === "mediator") {
          const med = await api.get("/mediations");
          setActiveMediations((med.data.items || []).filter(m => !["completed","cancelled","rejected"].includes(m.status)).slice(0, 5));
        }
        if (user?.role === "admin") {
          const s = await api.get("/admin/stats");
          setStats(s.data);
        }
      } catch (e) { console.warn("dashboard load:", e?.response?.status || e.message); }
    };
    fetchAll();
    // Auto-refresh dashboard every 15s for live counters
    const handle = setInterval(fetchAll, 15000);
    const onVis = () => { if (document.visibilityState === "visible") fetchAll(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(handle);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user]);

  return (
    <div className="container-app py-12" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="label-eyebrow">Panel · {
            user?.role === "lawyer" ? "Abogado" :
            user?.role === "mediator" ? "Mediador" :
            user?.role === "admin" ? "Administrador" :
            user?.role === "requester" ? "Persona solicitante" :
            "Usuario"
          }</div>
          <h1 className="serif text-5xl mt-3" data-testid="dashboard-greeting">Hola, {user?.name?.split(" ")[0]}.</h1>
        </div>
        <div className="flex items-center gap-3">
          {user?.role === "lawyer" && (
            <Link to="/app/delegaciones/nueva" className="btn-primary" data-testid="cta-new-delegation"><Plus size={16} className="mr-1.5"/> Nueva delegación</Link>
          )}
          {user?.role === "lawyer" && config.new_mediation_enabled && (
            <Link to="/mediacion-familiar" className="btn-ghost inline-flex items-center gap-1.5 border-cyan-400/30 text-cyan-200 hover:bg-cyan-400/10" data-testid="cta-mediacion-familiar-lawyer">
              <HeartHandshake size={14}/> Solicitar mediación familiar
            </Link>
          )}
          <Link to="/app/perfil" className="btn-ghost" data-testid="cta-perfil">Mi perfil</Link>
        </div>
      </div>

      {user?.role === "lawyer" && user?.plan && user?.plan !== "free" && (
        <Link to="/app/asistente-ia" className="mt-8 panel p-6 flex items-center gap-4 border-cyan-400/30 hover:bg-cyan-400/5 transition-colors group" data-testid="dashboard-ai-card">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400/30 to-emerald-400/20 border border-cyan-400/40 flex items-center justify-center shrink-0">
            <Sparkles size={22} className="text-cyan-300"/>
          </div>
          <div className="flex-1">
            <div className="label-eyebrow text-cyan-300">Beneficio Fundador</div>
            <div className="serif text-xl mt-1">Asistente IA Colega Link</div>
            <div className="text-xs text-muted-foreground mt-1">Redacta escritos, minutas, checklists y correos con Claude Sonnet 4.5. Uso ilimitado incluido en tu membresía.</div>
          </div>
          <ArrowUpRight size={16} className="text-cyan-300 group-hover:translate-x-0.5 transition-transform"/>
        </Link>
      )}

      {user?.role === "lawyer" && (!user?.plan || user?.plan === "free") && (
        <Link to="/app/asistente-ia" className="mt-8 panel p-6 flex items-center gap-4 border-amber-400/30 bg-amber-400/5 hover:bg-amber-400/10 transition-colors group" data-testid="dashboard-ai-upgrade-card">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400/25 to-cyan-400/15 border border-amber-400/40 flex items-center justify-center shrink-0">
            <Sparkles size={22} className="text-amber-300"/>
          </div>
          <div className="flex-1">
            <div className="label-eyebrow text-amber-300">Desbloquea con membresía</div>
            <div className="serif text-xl mt-1">Asistente IA Colega Link · Solo Fundadores</div>
            <div className="text-xs text-muted-foreground mt-1">Redacta escritos, resúmenes, minutas y checklists en segundos con IA jurídica. Incluido gratis en el Plan Fundador.</div>
          </div>
          <ArrowUpRight size={16} className="text-amber-300 group-hover:translate-x-0.5 transition-transform"/>
        </Link>
      )}

      {user?.profile_incomplete && (
        <div className="mt-8 panel p-5 ring-1 ring-amber-400/40 bg-amber-400/5 flex items-start gap-3 flex-wrap" data-testid="profile-incomplete-banner">          <div className="w-9 h-9 rounded-full bg-amber-400/15 flex items-center justify-center shrink-0"><Clock size={16} className="text-amber-300"/></div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-medium text-amber-100">Completa tu perfil</div>
            <div className="text-sm text-muted-foreground">
              Falta{(user?.profile_missing_fields || []).length === 1 ? "" : "n"}: <strong className="text-amber-200">{(user?.profile_missing_fields || []).join(", ") || "algunos datos administrativos"}</strong>. Estos datos son obligatorios para operar y quedan visibles sólo para administración.
            </div>
          </div>
          <Link to="/app/perfil" className="btn-primary text-sm py-2" data-testid="profile-incomplete-cta">Completar ahora</Link>
        </div>
      )}

      {!user?.verified && user?.role !== "admin" && (
        <div className="mt-8 panel p-5 flex items-center gap-3" data-testid="verification-banner">
          <Clock size={18} className="text-muted-foreground"/>
          <div>
            <div className="font-medium">Tu cuenta está en verificación.</div>
            <div className="text-sm text-muted-foreground">Un administrador revisará tus antecedentes en menos de 24 horas hábiles.</div>
          </div>
        </div>
      )}

      {/* Tarjeta destacada del Reto Pionero (sólo Pioneros/Embajadores activos) */}
      {user?.role === "lawyer" && ["pionero", "embajador_pionero", "founder"].includes(user?.plan) && (
        <div className="mt-8">
          <PioneroChallengeCard />
        </div>
      )}

      {/* Community CTA — visible sólo para profesionales (lawyer/mediator), no solicitantes ni admin */}
      {(user?.role === "lawyer" || user?.role === "mediator") && (
        <div className="mt-4 panel p-5 ring-1 ring-emerald-400/30 bg-gradient-to-r from-emerald-950/30 to-background flex items-center gap-4 flex-wrap" data-testid="comunidad-banner">
          <div className="w-10 h-10 rounded-full bg-emerald-400/15 flex items-center justify-center shrink-0 ring-1 ring-emerald-400/40">
            <span className="text-xl">💬</span>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="font-medium">Únete al grupo WhatsApp de Colega Link</div>
            <div className="text-xs text-muted-foreground mt-0.5">Comunidad cerrada · clases informativas · novedades antes que nadie.</div>
          </div>
          <Link to="/comunidad" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium bg-[#25D366] text-white hover:bg-[#1ebe5d] transition-colors" data-testid="dashboard-comunidad-cta">
            Ver opciones
          </Link>
        </div>
      )}

      {/* Membership widget — visible only for lawyer/mediator */}
      {(user?.role === "lawyer" || user?.role === "mediator") && (
        <MembershipWidget user={user} />
      )}

      {user?.role === "admin" && stats && (
        <section className="mt-10 grid grid-cols-2 md:grid-cols-4 grid-borders border rounded-lg overflow-hidden" style={{ borderColor: "hsl(var(--border))" }}>
          <Stat label="Usuarios" value={stats.users}/>
          <Stat label="Abogados" value={stats.lawyers}/>
          <Stat label="Mediadores" value={stats.mediators}/>
          <Stat label="Por verificar" value={stats.pending_verifications} accent/>
          <Stat label="Delegaciones" value={stats.delegations}/>
          <Stat label="Abiertas" value={stats.open_delegations}/>
          <Stat label="Completadas" value={stats.completed_delegations}/>
          <Stat label="Leads Fundadores" value={stats.early_access}/>
        </section>
      )}

      {/* ASSIGNED TO ME — top priority section for the delegated lawyer */}
      {user?.role === "lawyer" && assignedToMe.length > 0 && (
        <section className="mt-12">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="label-eyebrow text-[hsl(var(--primary))]">Tu trabajo en curso</div>
              <h2 className="serif text-3xl mt-1">Asignadas a ti ({assignedToMe.length})</h2>
            </div>
            <Link to="/app/delegaciones?mine=assigned&status=" className="text-sm link-underline" data-testid="see-all-assigned">Ver todo</Link>
          </div>
          <ul className="space-y-3">
            {assignedToMe.map((d) => <DelegationRow key={d.id} d={d} highlight/>)}
          </ul>
        </section>
      )}

      {/* MEDIATIONS for both roles */}
      {activeMediations.length > 0 && (
        <section className="mt-12">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="label-eyebrow">Mediaciones activas</div>
              <h2 className="serif text-3xl mt-1 inline-flex items-center gap-3"><Handshake size={28} className="text-[hsl(var(--primary))]"/>Mediaciones</h2>
            </div>
            <Link to="/app/mediaciones" className="text-sm link-underline">Ver todo</Link>
          </div>
          <ul className="space-y-2">
            {activeMediations.map((m) => (
              <li key={m.id} className="panel p-4 flex items-center justify-between gap-3 text-sm" data-testid={`mediation-summary-${m.id}`}>
                <Link to={`/app/mediaciones/${m.id}`} className="link-underline serif text-lg truncate">{m.subject}</Link>
                <MediationStatusPill status={m.status}/>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-12 grid lg:grid-cols-3 gap-8">
        {(user?.role === "lawyer" || user?.role === "admin") && (
          <div className="lg:col-span-2">
            <div className="flex items-end justify-between mb-4">
              <h2 className="serif text-3xl">Delegaciones abiertas</h2>
              <Link to="/app/delegaciones" className="text-sm link-underline" data-testid="see-all-delegations">Ver todo</Link>
            </div>
            {open.length === 0 ? (
              <EmptyState text="No hay delegaciones abiertas en este momento."/>
            ) : (
              <ul className="space-y-3">
                {open.map((d) => <DelegationRow key={d.id} d={d} />)}
              </ul>
            )}
          </div>
        )}

        <aside className="space-y-6">
          {user?.role === "lawyer" && (
            <div className="panel p-6">
              <div className="label-eyebrow">Tus publicaciones</div>
              <h3 className="serif text-2xl mt-2 inline-flex items-center gap-2"><Briefcase size={18}/>Mis delegaciones</h3>
              {mine.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-3">Aún no has publicado.</p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {mine.map((d) => (
                    <li key={d.id} className="flex items-center justify-between gap-3 text-sm">
                      <Link to={`/app/delegaciones/${d.id}`} className="link-underline truncate">{d.title}</Link>
                      <StatusBadge status={d.status}/>
                    </li>
                  ))}
                </ul>
              )}
              <Link to="/app/delegaciones?mine=owned&status=" className="btn-ghost w-full mt-5 text-sm">Ver todas</Link>
            </div>
          )}
          <div className="panel p-6">
            <div className="label-eyebrow">Directorio</div>
            <h3 className="serif text-2xl mt-2">Mediadores familiares</h3>
            <p className="text-sm text-muted-foreground mt-2">Encuentra un mediador inscrito por región y comuna.</p>
            <Link to="/app/mediadores" className="btn-ghost w-full mt-5 text-sm" data-testid="cta-mediadores-aside">Buscar mediadores</Link>
          </div>
        </aside>
      </section>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="p-6 bg-[hsl(var(--card))]">
      <div className="label-eyebrow">{label}</div>
      <div className={`mt-2 serif text-4xl ${accent ? "brand-gradient-text" : ""}`}>{value ?? 0}</div>
    </div>
  );
}

function DelegationRow({ d, highlight = false }) {
  return (
    <li className={`panel p-5 hover:border-foreground/40 transition-colors ${highlight ? "ring-1 ring-[hsl(var(--primary))]" : ""}`} data-testid={`delegation-row-${d.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/app/delegaciones/${d.id}`} className="serif text-xl link-underline">{d.title}</Link>
            <span className="pill capitalize text-xs">{d.task_type}</span>
            <StatusBadge status={d.status}/>
          </div>
          <div className="mt-2 text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1.5"><MapPin size={13}/>{d.comuna}, {d.region}</span>
            <span className="inline-flex items-center gap-1.5"><Clock size={13}/>{d.date} · {d.time}</span>
            <span className="truncate">{d.court}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="serif text-2xl">{formatCLP(d.fee_clp)}</div>
          {d.status === "open" && <div className="label-eyebrow mt-1">{d.bids_count || 0} postulaciones</div>}
        </div>
      </div>
    </li>
  );
}

function StatusBadge({ status }) {
  const map = {
    open: { l: "Abierta", cls: "badge-cyan" },
    assigned: { l: "Asignada · Pago Protegido pendiente", cls: "badge-pending" },
    in_progress: { l: "En curso", cls: "badge-cyan" },
    awaiting_approval: { l: "Acta · esperando", cls: "badge-pending" },
    completed: { l: "Completada", cls: "badge-verified" },
    cancelled: { l: "Cancelada", cls: "" },
  };
  const s = map[status] || { l: status, cls: "" };
  return <span className={`pill text-xs ${s.cls}`}>{s.l}</span>;
}

function EmptyState({ text }) {
  return <div className="panel p-10 text-center text-muted-foreground"><Star className="mx-auto mb-3" size={18}/>{text}</div>;
}

function MediationStatusPill({ status }) {
  const map = {
    proposed: { l: "Propuesta", cls: "badge-cyan" },
    pending: { l: "Pendiente", cls: "badge-pending" },
    accepted: { l: "Aceptada", cls: "badge-cyan" },
    scheduled: { l: "Agendada", cls: "badge-cyan" },
    in_progress: { l: "En curso", cls: "badge-cyan" },
    completed: { l: "Completada", cls: "badge-verified" },
    cancelled: { l: "Cancelada", cls: "" },
    rejected: { l: "Rechazada", cls: "" },
  };
  const s = map[status] || { l: status, cls: "" };
  return <span className={`pill text-xs ${s.cls}`}>{s.l}</span>;
}

function MembershipWidget({ user }) {
  const [m, setM] = useState(null);
  useEffect(() => {
    api.get("/membership/me").then((r) => setM(r.data)).catch((e) => console.warn("membership/me:", e?.response?.status || e.message));
  }, []);
  if (!m) return null;

  const ICONS = { free: Shield, pro: Zap, premium: Sparkles, founder: Crown };
  const Icon = ICONS[m.plan] || Shield;
  const isFree = m.plan === "free";
  const accentColor = m.plan === "founder" ? "text-amber-400" : m.plan === "premium" ? "text-violet-400" : m.plan === "pro" ? "text-emerald-400" : "text-muted-foreground";
  const ringCls = m.plan === "founder" ? "ring-1 ring-amber-400/30" : "";

  return (
    <section className={`mt-8 panel p-6 ${ringCls}`} data-testid="dashboard-membership-widget">
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-1">
          <div className="flex items-center gap-2">
            <Icon size={20} className={accentColor} />
            <div className="label-eyebrow">Tu plan</div>
          </div>
          <h3 className="serif text-2xl mt-2">{m.plan_definition.name}{m.is_founder && m.founder_number ? ` · N°${m.founder_number}` : ""}</h3>
          <p className="text-xs text-muted-foreground mt-1">Comisión {m.plan_definition.commission_pct}% · {m.plan_definition.priority_label}</p>
          {isFree ? (
            <Link to="/planes" className="btn-primary mt-4 inline-flex items-center gap-2 text-sm" data-testid="widget-upgrade-cta">
              <Crown size={14} /> Ver planes <ArrowUpRight size={12} className="ml-1" />
            </Link>
          ) : (
            <Link to="/app/membresia" className="btn-ghost mt-4 inline-flex items-center gap-2 text-sm" data-testid="widget-manage-link">
              Gestionar membresía
            </Link>
          )}
        </div>
        <div className="md:col-span-2 grid sm:grid-cols-2 gap-4">
          <div className="panel p-4">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Delegaciones gratis</span>
              <span>{m.free_delegations_used}/{m.plan_definition.free_delegations}</span>
            </div>
            <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-[hsl(var(--accent))]" style={{ width: m.plan_definition.free_delegations > 0 ? `${(m.free_delegations_used / m.plan_definition.free_delegations) * 100}%` : "0%" }} />
            </div>
            <div className="mt-2 text-xs">
              {m.free_delegations_remaining > 0 ? (
                <span><span className="text-foreground font-medium">{m.free_delegations_remaining}</span> con 0% comisión</span>
              ) : isFree ? (
                <span className="text-amber-300">Mejora tu plan para obtener delegaciones gratis</span>
              ) : (
                <span className="text-muted-foreground">Cuota agotada — renueva el próximo mes</span>
              )}
            </div>
          </div>
          <div className="panel p-4">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Aviso prioritario de delegaciones</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-400/15 text-emerald-300 font-bold tracking-wider">ACTIVO</span>
            </div>
            <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-400/70" style={{ width: "100%" }} />
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Aviso alto para nuevas delegaciones · beneficio de tu plan {m.plan_definition.name || "actual"}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
